import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PermissionsApiService } from '@p2p/access-control/api';
import { LocalStorageService } from '@p2p/shared/utils';
import { catchError, map, of, switchMap, take, tap, withLatestFrom } from 'rxjs';

import { LS_TOKEN_KEY } from '../constants/common';
import { AccountDataService } from '../infrastructure/account-data.service';
import { LoginDataService } from '../infrastructure/login-data.service';

import * as AuthActions from './auth.actions';
import { AuthFacade } from './auth.facade';

@Injectable()
export class AuthEffects {
    private readonly actions$ = inject(Actions);
    private readonly accountDataService = inject(AccountDataService);
    private readonly authFacade = inject(AuthFacade);
    private readonly localStorageService = inject(LocalStorageService);
    private readonly loginDataService = inject(LoginDataService);
    private readonly permissionsApiService = inject(PermissionsApiService);
    private readonly router = inject(Router);

    checkAuthentication$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.checkAuthentication),
            switchMap(() =>
                this.accountDataService.getCurrent().pipe(
                    withLatestFrom(this.authFacade.urlAfterLogin$.pipe(take(1))),
                    map(([account, urlAfterLogin]) => {
                        this.authFacade.setIsAuthenticated(true);
                        this.authFacade.setToken(this.localStorageService.getItem(LS_TOKEN_KEY) as string);
                        this.authFacade.accountInitiallyRequested();
                        this.permissionsApiService.loadPermissions(account.role);
                        this.router.navigateByUrl(urlAfterLogin ? urlAfterLogin : '/dashboards');

                        return AuthActions.setAccount({ account });
                    }),
                    catchError(() => {
                        this.authFacade.setIsAuthenticated(false);
                        this.authFacade.accountInitiallyRequested();

                        return of(AuthActions.authenticationFailure({ error: null }));
                    })
                )
            )
        )
    );

    register$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.register),
            switchMap(({ password, token }) =>
                this.loginDataService.register({ password, token }).pipe(
                    map(({ token }) => {
                        this.localStorageService.setItem(LS_TOKEN_KEY, token);
                        this.authFacade.checkAuthentication();

                        return AuthActions.authenticationSuccess();
                    }),
                    catchError((error) => {
                        return of(AuthActions.authenticationFailure({ error }));
                    })
                )
            )
        )
    );

    authenticate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.authenticate),
            switchMap(({ email, password }) =>
                this.loginDataService.login({ email: email.toLowerCase(), password }).pipe(
                    map(({ token }) => {
                        this.localStorageService.setItem(LS_TOKEN_KEY, token);
                        this.authFacade.checkAuthentication();

                        return AuthActions.authenticationSuccess();
                    }),
                    catchError((error) => {
                        return of(AuthActions.authenticationFailure({ error }));
                    })
                )
            )
        )
    );
}
